import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { GraduationCap, Heart, Shield, User, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolSignup = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState('teacher');
  const [form, setForm] = useState({ username: '', email: '', password: '', school_name: '', grade_level: 7 });
  const [submitting, setSubmitting] = useState(false);

  const roles = [
    { id: 'student', label: 'Student', icon: <User className="w-7 h-7" />, color: 'from-blue-500 to-cyan-600', desc: 'Learn with your school class' },
    { id: 'teacher', label: 'Teacher', icon: <GraduationCap className="w-7 h-7" />, color: 'from-indigo-500 to-purple-600', desc: 'Create classes, track students' },
    { id: 'parent', label: 'Parent', icon: <Heart className="w-7 h-7" />, color: 'from-pink-500 to-rose-600', desc: 'Monitor your child\'s progress' },
    { id: 'admin', label: 'Admin', icon: <Shield className="w-7 h-7" />, color: 'from-amber-600 to-red-600', desc: 'Manage the whole school' },
  ];

  const submit = async () => {
    if (!form.username || !form.email || !form.password) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be 6+ chars'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/school/register`, { ...form, role });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Welcome, ${form.username}!`);
      // Redirect by role
      const target = role === 'parent' ? '/parent'
                   : role === 'student' ? '/dashboard'
                   : '/teacher';
      window.location.href = target;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <Link to="/" className="text-white/70 hover:text-white inline-flex items-center gap-2 mb-6 font-semibold">
          <ArrowLeft className="w-5 h-5" />Back to home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white/95 backdrop-blur rounded-3xl p-6 sm:p-8 shadow-2xl">
          <h1 className="text-3xl font-black text-[#1E293B] mb-1">Join as a School User</h1>
          <p className="text-[#64748B] mb-6 text-sm">Sign up as a Teacher, Parent, or School Admin</p>

          {/* Role picker */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            {roles.map(r => (
              <motion.button key={r.id} onClick={() => setRole(r.id)} whileTap={{ scale: 0.95 }} data-testid={`role-${r.id}`}
                className={`p-3 rounded-2xl border-2 text-center transition ${role === r.id ? `bg-gradient-to-br ${r.color} text-white border-transparent shadow-lg` : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                <div className="flex justify-center mb-1">{r.icon}</div>
                <p className="font-bold text-sm">{r.label}</p>
              </motion.button>
            ))}
          </div>
          <p className="text-xs text-[#64748B] text-center mb-5 italic">{roles.find(r => r.id === role)?.desc}</p>

          {/* Form */}
          <div className="space-y-3">
            <input type="text" placeholder="Full name / username" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-indigo-500 outline-none"
              data-testid="school-username" />
            <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-indigo-500 outline-none"
              data-testid="school-email" />
            <input type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-indigo-500 outline-none"
              data-testid="school-password" />
            {role === 'student' && (
              <select value={form.grade_level} onChange={e => setForm({ ...form, grade_level: parseInt(e.target.value) })}
                className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-indigo-500 outline-none"
                data-testid="school-grade">
                <option value="6">Grade 6</option>
                <option value="7">Grade 7</option>
                <option value="8">Grade 8</option>
              </select>
            )}
            {role !== 'parent' && role !== 'student' && (
              <input type="text" placeholder="School name (optional)" value={form.school_name} onChange={e => setForm({ ...form, school_name: e.target.value })}
                className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-indigo-500 outline-none"
                data-testid="school-name" />
            )}
            
            <motion.button onClick={submit} disabled={submitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              data-testid="school-signup-submit"
              className={`w-full py-3.5 bg-gradient-to-r ${roles.find(r => r.id === role)?.color} text-white rounded-xl font-black text-base flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg`}>
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />Creating account...</> : `Sign up as ${roles.find(r => r.id === role)?.label}`}
            </motion.button>
          </div>

          <p className="text-center mt-5 text-sm text-[#64748B]">
            Already registered? <Link to="/login" className="text-indigo-600 font-bold hover:underline">Login</Link>
            {' '}·{' '}<Link to="/signup" className="text-indigo-600 font-bold hover:underline">Student signup</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SchoolSignup;
